import { Badge, Tag } from "@appkit4/react-components";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import { type AiInsightsResponse, getAiInsightsAvailabilityMessage, insightTypeForRaid, useAiInsightJob } from "../../../api/aiInsights";
import { useGetDependencyItems } from "../../../api/dependencies";
import { isApiError } from "../../../api/errors";
import { AiOutputFeedbackControls } from "../../aiOutputFeedback/AiOutputFeedbackControls";
import { Spinner } from "../../shared";
import {
  AiInsightDeveloperContext,
  AiInsightSourceMarker,
  AiInsightsRaidTopItemCard,
} from "../AiInsightsRaidDetailParts";
import { getTagColor } from "../aiInsightTagColor";
import { AiInsightWaitingState } from "../AiInsightWaitingState";
import { AiInsightsRaidDependenciesDetailModal } from "./AiInsightsRaidDependenciesDetailModal";
import type {
  RaidDependenciesClashDto,
  RaidDependenciesInsightDto,
  RaidDependenciesNetworkObservationDto,
  RaidDependenciesPotentialUnrecordedItemDto,
  RaidDependenciesTopItemDto,
} from "./AiInsightsRaidDependenciesTypes";

const RAID_DEPENDENCIES_TAB = "dependencies" as const;
const PERSONA_NOT_ASSIGNED_ERROR_CODE = "no_persona";

export type AiInsightsRaidDependenciesShelfProps = {
  isOpen: boolean;
  insightResponse?: AiInsightsResponse;
};

export function AiInsightsRaidDependenciesShelf({ isOpen, insightResponse }: AiInsightsRaidDependenciesShelfProps) {
  const dependenciesQuery = useAiInsightJob(insightTypeForRaid(RAID_DEPENDENCIES_TAB), isOpen && insightResponse == null);
  const [selectedDependency, setSelectedDependency] = useState<RaidDependenciesTopItemDto | null>(
    null
  );
  const [expandedClashIndex, setExpandedClashIndex] = useState<number | null>(null);
  const [expandedUnrecordedIndex, setExpandedUnrecordedIndex] = useState<number | null>(null);
  const [expandedObservationIndex, setExpandedObservationIndex] = useState<number | null>(null);

  const dependenciesResponse = insightResponse ?? dependenciesQuery.data?.data;
  const dependenciesInsight = dependenciesResponse?.insight as
    | RaidDependenciesInsightDto
    | undefined;
  const dependencyLookup = useGetDependencyItems(
    dependenciesResponse?.dealDataVersionId != null
      ? { dealDataVersionId: dependenciesResponse.dealDataVersionId }
      : undefined,
    isOpen && dependenciesInsight != null
  );
  const dependenciesData = dependencyLookup.data;
  const dependenciesById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof dependenciesData>[number]>();
    for (const dep of dependenciesData ?? []) {
      if (dep.sourceDepId != null && dep.sourceDepId.trim().length > 0) {
        map.set(dep.sourceDepId.trim().toLowerCase(), dep);
      }
      if (dep.dependencyItemId != null) {
        const idStr = String(dep.dependencyItemId).toLowerCase();
        map.set(idStr, dep);
        map.set(`dep-${idStr}`, dep);
      }
    }
    return map;
  }, [dependenciesData]);
  const isDependenciesLoading =
    isOpen &&
    dependenciesQuery.isLoading &&
    dependenciesInsight == null;
  const dependenciesUnderlyingError = dependenciesQuery.error;
  const isPersonaNotAssignedError =
    isApiError(dependenciesUnderlyingError) &&
    dependenciesUnderlyingError.code === PERSONA_NOT_ASSIGNED_ERROR_CODE;
  const isNoDataError =
    isApiError(dependenciesUnderlyingError) && dependenciesUnderlyingError.code === "no_data";
  const isReadinessWaiting = dependenciesQuery.mappingReason != null;
  const dependenciesError =
    isOpen && !isDependenciesLoading && dependenciesInsight == null
      ? isPersonaNotAssignedError
        ? "No AI Insights persona is assigned to your account yet."
        : dependenciesQuery.isError && !isNoDataError && !isReadinessWaiting
          ? "Unable to load insights right now. Please try again shortly."
          : null
      : null;
  const dependenciesUnavailableMessage =
    isOpen && !isDependenciesLoading && dependenciesInsight == null
      ? getAiInsightsAvailabilityMessage(
          isApiError(dependenciesUnderlyingError) ? dependenciesUnderlyingError.code : undefined,
          "Dependencies"
        )
      : null;

  const hasShownPersonaToastRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasShownPersonaToastRef.current = false;
      return;
    }
    if (isPersonaNotAssignedError && !hasShownPersonaToastRef.current) {
      hasShownPersonaToastRef.current = true;
      toast.error(
        "No AI Insights persona is assigned to your account yet. Contact an admin to get one assigned."
      );
    }
  }, [isOpen, isPersonaNotAssignedError]);

  if (isDependenciesLoading) {
    return (
      <section
        className="global-insights-overlay__section global-insights-overlay__section--loading"
        aria-live="polite"
      >
        <Spinner size="lg" />
      </section>
    );
  }

  if (dependenciesError != null) {
    return (
      <section className="global-insights-overlay__section" aria-live="assertive">
        <p className="global-insights-overlay__raid-status-text global-insights-overlay__raid-status-text--error">
          {dependenciesError}
        </p>
      </section>
    );
  }

  if (dependenciesUnavailableMessage != null) {
    return isReadinessWaiting ? (
      <AiInsightWaitingState reason={dependenciesQuery.mappingReason} />
    ) : (
      <section className="global-insights-overlay__section" aria-live="polite">
        <p className="global-insights-overlay__raid-status-text">
          {dependenciesUnavailableMessage}
        </p>
      </section>
    );
  }

  if (dependenciesInsight == null) {
    return null;
  }

  const topDependencies = Array.isArray(dependenciesInsight.top_dependencies)
    ? dependenciesInsight.top_dependencies
    : [];
  const clashes = Array.isArray(dependenciesInsight.dependency_clashes)
    ? dependenciesInsight.dependency_clashes
    : [];
  const potentialUnrecorded = Array.isArray(dependenciesInsight.potential_unrecorded_dependencies)
    ? dependenciesInsight.potential_unrecorded_dependencies
    : [];
  const networkObservations = Array.isArray(dependenciesInsight.dependency_network_observations)
    ? dependenciesInsight.dependency_network_observations
    : [];
  return (
    <>
      <section
        className="global-insights-overlay__section global-insights-overlay__section--raid-health"
        aria-labelledby="global-insights-raid-dependencies-health-title"
      >
        <div className="global-insights-overlay__section-header">
          <h3
            id="global-insights-raid-dependencies-health-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Overall dependencies health
          </h3>
          <AiInsightSourceMarker />
        </div>
        <div className="global-insights-overlay__raid-health-card">
          <p className="global-insights-overlay__raid-health-text">
            {dependenciesInsight.headline ?? "Not available"}
          </p>
        </div>
        {dependenciesResponse?.status === "ready" && (
          <AiOutputFeedbackControls
            executionId={dependenciesResponse.aiOutputExecutionId}
            outputType="ai_insight"
            className="global-insights-overlay__feedback"
          />
        )}
      </section>

      <section
        className="global-insights-overlay__section"
        aria-labelledby="global-insights-raid-dependencies-drivers-title"
      >
        <h3
          id="global-insights-raid-dependencies-drivers-title"
          className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
        >
          Top dependencies requiring attention
        </h3>

        {topDependencies.length > 0 ? (
          <div className="global-insights-overlay__raid-driver-list">
            {topDependencies.map((item, index) => {
              const sourceDependency = dependenciesById.get(
                item.dependency_id?.toLowerCase() ?? ""
              );
              return (
                <AiInsightsRaidTopItemCard
                  key={item?.dependency_id ?? item?.title ?? `dependency-${index}`}
                  itemId={sourceDependency?.sourceDepId ?? item.dependency_id}
                  title={sourceDependency?.description ?? item.title}
                  primaryMeta={sourceDependency?.workstream ?? item.providing_workstream}
                  secondaryMeta={sourceDependency?.dependentWorkstream ?? item.dependent_workstream}
                  owner={sourceDependency?.owner ?? item.dependency_requirement?.provider_or_owner}
                  primaryMetaLabel="Provider"
                  secondaryMetaLabel="Dependent"
                  metaLayout="aligned"
                  aiGenerated
                  onOpen={() => setSelectedDependency(item)}
                />
              );
            })}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">
            No top dependencies available.
          </p>
        )}
      </section>

      <ExpandableSection
        heading="Dependency network observations"
        emptyText="No dependency network observations available."
        items={networkObservations}
        expandedIndex={expandedObservationIndex}
        onToggle={(index) =>
          setExpandedObservationIndex((current) => (current === index ? null : index))
        }
        renderItem={(item) => <NetworkObservationContent item={item} />}
        getKey={(item, index) => `${item.observation_type ?? "observation"}-${index}`}
        getMeta={(item) => (
          <>
            <NetworkObservationTypeBadge value={item.observation_type} />
            <ConfidenceValueBadge value={item.confidence} />
          </>
        )}
        getTitle={getNetworkObservationTitle}
        aiGenerated
      />

      {clashes.length > 0 && (
        <ExpandableSection
          heading="Dependency clashes"
          emptyText="No dependency clashes available."
          items={clashes}
          expandedIndex={expandedClashIndex}
          onToggle={(index) => setExpandedClashIndex((current) => (current === index ? null : index))}
          renderItem={(item) => <DependencyClashContent item={item} />}
          getKey={(item, index) => item.clash_id ?? `dependency-clash-${index}`}
          getMeta={(item) => (
            <>
              <ClashSeverityBadge value={item.severity} />
              {item.clash_id && <span>Clash ID: {item.clash_id}</span>}
            </>
          )}
          getTitle={(item) => item.clash_type ?? "Not available"}
          aiGenerated
        />
      )}

      <section className="global-insights-overlay__section">
        <h3 className="global-insights-overlay__section-title global-insights-overlay__section-title--raid">
          Potential unrecorded dependencies
        </h3>
        {potentialUnrecorded.length > 0 ? (
          <div className="global-insights-overlay__raid-unrecorded-list">
            {potentialUnrecorded.map((candidate, index) => (
              <UnrecordedDependencyCard
                key={candidate.suggested_title ?? `potential-dependency-${index}`}
                candidate={candidate}
                isExpanded={expandedUnrecordedIndex === index}
                onToggle={() =>
                  setExpandedUnrecordedIndex((current) => (current === index ? null : index))
                }
              />
            ))}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">
            No potential unrecorded dependencies available.
          </p>
        )}
      </section>


      <AiInsightDeveloperContext developerContext={{ asAt: dependenciesInsight.as_at, dataUsed: dependenciesInsight.context_summary?.data_used, missingInputs: dependenciesInsight.context_summary?.data_missing_or_restricted, limitations: dependenciesInsight.data_quality_and_caveats }} />

      {selectedDependency != null && (
        <AiInsightsRaidDependenciesDetailModal
          item={selectedDependency}
          sourceDependency={dependenciesById.get(
            selectedDependency.dependency_id?.toLowerCase() ?? ""
          )}
          clashes={clashes.filter(
            (clash) => clash.related_dependency_id === selectedDependency.dependency_id
          )}
          onClose={() => setSelectedDependency(null)}
        />
      )}
    </>
  );
}

type ExpandableSectionProps<T> = {
  heading: string;
  emptyText: string;
  items: T[];
  expandedIndex: number | null;
  onToggle: (index: number) => void;
  renderItem: (item: T) => ReactNode;
  getKey: (item: T, index: number) => string;
  getMeta: (item: T) => ReactNode;
  getTrailingMeta?: (item: T) => ReactNode;
  getTitle: (item: T) => string;
  aiGenerated?: boolean;
};

function ExpandableSection<T>({
  heading,
  emptyText,
  items,
  expandedIndex,
  onToggle,
  renderItem,
  getKey,
  getMeta,
  getTrailingMeta,
  getTitle,
  aiGenerated = false,
}: ExpandableSectionProps<T>) {
  return (
    <section className="global-insights-overlay__section">
      <div className="global-insights-overlay__section-header">
        <h3 className="global-insights-overlay__section-title global-insights-overlay__section-title--raid">
          {heading}
        </h3>
        {aiGenerated && <AiInsightSourceMarker />}
      </div>
      {items.length > 0 ? (
        <div className="global-insights-overlay__raid-unrecorded-list">
          {items.map((item, index) => {
            const isExpanded = expandedIndex === index;

            return (
              <div
                key={getKey(item, index)}
                className="global-insights-overlay__raid-unrecorded-card"
              >
                <button
                  type="button"
                  className="global-insights-overlay__raid-unrecorded-header"
                  onClick={() => onToggle(index)}
                  aria-expanded={isExpanded}
                >
                  <div className="global-insights-overlay__raid-unrecorded-summary">
                    <div className="global-insights-overlay__raid-unrecorded-meta">
                      {getMeta(item)}
                    </div>
                    <h4 className="global-insights-overlay__raid-unrecorded-title">
                      {getTitle(item)}
                    </h4>
                  </div>
                  <div className="global-insights-overlay__raid-unrecorded-header-actions">
                    {getTrailingMeta != null && getTrailingMeta(item)}
                    <span
                      className={[
                        "global-insights-overlay__raid-unrecorded-chevron",
                        isExpanded
                          ? "global-insights-overlay__raid-unrecorded-chevron--expanded"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      aria-hidden="true"
                    >
                      <span className="Appkit4-icon icon-down-chevron-outline" />
                    </span>
                  </div>
                </button>
                {isExpanded && (
                  <div className="global-insights-overlay__raid-unrecorded-content">
                    {renderItem(item)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="global-insights-overlay__raid-status-text">{emptyText}</p>
      )}
    </section>
  );
}

export function DependencyClashContent({ item }: { item: RaidDependenciesClashDto }) {
  const linkedItems = [
    item.predecessor_item_id != null && item.predecessor_item_id.trim().length > 0
      ? `Predecessor: ${item.predecessor_item_id.trim()}`
      : null,
    item.dependent_item_id != null && item.dependent_item_id.trim().length > 0
      ? `Dependent: ${item.dependent_item_id.trim()}`
      : null,
  ].filter((entry): entry is string => entry != null);

  return (
    <>
      <p className="global-insights-overlay__raid-unrecorded-label">Why flagged</p>
      <p className="global-insights-overlay__raid-unrecorded-text">
        {item.why_flagged ?? "Not available"}
      </p>
      <hr className="global-insights-overlay__raid-unrecorded-divider" />
      <p className="global-insights-overlay__raid-unrecorded-label">Possible impact</p>
      <p className="global-insights-overlay__raid-unrecorded-text">
        {item.possible_impact ?? "Not available"}
      </p>
      <hr className="global-insights-overlay__raid-unrecorded-divider" />
      <p className="global-insights-overlay__raid-unrecorded-label">Resolution needed</p>
      <p className="global-insights-overlay__raid-unrecorded-text">
        {item.resolution_needed ?? "Not available"}
      </p>
      <hr className="global-insights-overlay__raid-unrecorded-divider" />
      <p className="global-insights-overlay__raid-unrecorded-label">Linked items</p>
      <TagCollection values={linkedItems} emptyText="Not available" />
      <p className="global-insights-overlay__raid-unrecorded-meta">
        Confidence: {item.confidence ?? "Not available"}
      </p>
    </>
  );
}

type UnrecordedDependencyCardProps = {
  candidate: RaidDependenciesPotentialUnrecordedItemDto;
  isExpanded: boolean;
  onToggle: () => void;
};

function UnrecordedDependencyCard({
  candidate,
  isExpanded,
  onToggle,
}: UnrecordedDependencyCardProps) {
  const possibleMatchIds = [
    ...new Set(candidate.possible_existing_dependency_match?.match(/\b[A-Z]+-\d+\b/g) ?? []),
  ];
  const affectedWorkstream = candidate.dependent_workstream?.trim();
  const evidenceIds = Array.isArray(candidate.evidence_ids) ? candidate.evidence_ids : [];

  return (
    <div className="global-insights-overlay__raid-unrecorded-card global-insights-overlay__raid-unrecorded-card--ai-generated">
      <AiInsightSourceMarker />
      <button
        type="button"
        className="global-insights-overlay__raid-unrecorded-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="global-insights-overlay__raid-unrecorded-summary">
          <div className="global-insights-overlay__raid-unrecorded-meta">
            {`Confidence: ${candidate.confidence ?? "Not available"}`}
          </div>
          <h4 className="global-insights-overlay__raid-unrecorded-title">
            {candidate.suggested_title ?? "Not available"}
          </h4>
        </div>
        <span
          className={[
            "global-insights-overlay__raid-unrecorded-chevron",
            isExpanded ? "global-insights-overlay__raid-unrecorded-chevron--expanded" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          <span className="Appkit4-icon icon-down-chevron-outline" />
        </span>
      </button>

      {isExpanded && (
        <div className="global-insights-overlay__raid-unrecorded-content">
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate.why_flagged ?? "Not available"}
          </p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Apparent prerequisite</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate.potential_prerequisite ?? "Not available"}
          </p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">
            Possible schedule implications
          </p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate.schedule_implication ?? "Not available"}
          </p>
          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Validation question</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate.validation_question ?? "Not available"}
          </p>
          {(possibleMatchIds.length > 0 || affectedWorkstream != null) && (
            <>
              <hr className="global-insights-overlay__raid-unrecorded-divider" />
              <div className="global-insights-overlay__raid-unrecorded-tags">
                {possibleMatchIds.length > 0 && (
                  <span className="global-insights-overlay__raid-unrecorded-tags-label">
                    Possible match:
                  </span>
                )}
                {possibleMatchIds.map((possibleMatchId) => (
                  <Tag
                    key={possibleMatchId}
                    label={possibleMatchId}
                    type="outlined"
                    size="sm"
                    closable={false}
                    color={getTagColor(possibleMatchId)}
                  />
                ))}
                {affectedWorkstream != null && (
                  <Tag
                    label={affectedWorkstream}
                    type="outlined"
                    size="sm"
                    closable={false}
                    color={getTagColor(affectedWorkstream)}
                  />
                )}
              </div>
            </>
          )}
          {evidenceIds.length > 0 && (
            <p className="global-insights-overlay__raid-unrecorded-text">{`Evidence: ${evidenceIds.join(", ")}`}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function PotentialDependencyContent({ item }: { item: RaidDependenciesPotentialUnrecordedItemDto }) {
  return (
    <>
      <p className="global-insights-overlay__raid-unrecorded-text">{item.why_flagged ?? "Not available"}</p>
      <hr className="global-insights-overlay__raid-unrecorded-divider" />
      <p className="global-insights-overlay__raid-unrecorded-label">Apparent prerequisite</p>
      <p className="global-insights-overlay__raid-unrecorded-text">{item.potential_prerequisite ?? "Not available"}</p>
      <hr className="global-insights-overlay__raid-unrecorded-divider" />
      <p className="global-insights-overlay__raid-unrecorded-label">Possible schedule implications</p>
      <p className="global-insights-overlay__raid-unrecorded-text">{item.schedule_implication ?? "Not available"}</p>
      <hr className="global-insights-overlay__raid-unrecorded-divider" />
      <p className="global-insights-overlay__raid-unrecorded-label">Validation question</p>
      <p className="global-insights-overlay__raid-unrecorded-text">{item.validation_question ?? "Not available"}</p>
    </>
  );
}

export function NetworkObservationContent({ item }: { item: RaidDependenciesNetworkObservationDto }) {
  const affectedItems = Array.isArray(item.affected_items)
    ? item.affected_items.filter(
        (entry): entry is string => entry != null && entry.trim().length > 0
      )
    : [];
  const affectedWorkstreams = Array.isArray(item.affected_workstreams)
    ? item.affected_workstreams.filter(
        (entry): entry is string => entry != null && entry.trim().length > 0
      )
    : [];

  return (
    <>
      <p className="global-insights-overlay__raid-unrecorded-label">Description</p>
      <p className="global-insights-overlay__raid-unrecorded-text">
        {item.description ?? "Not available"}
      </p>
      <hr className="global-insights-overlay__raid-unrecorded-divider" />
      <p className="global-insights-overlay__raid-unrecorded-label">Affected items</p>
      <TagCollection values={affectedItems} emptyText="Not available" />
      <p className="global-insights-overlay__raid-unrecorded-label">Affected workstreams</p>
      <TagCollection values={affectedWorkstreams} emptyText="Not available" />
      <hr className="global-insights-overlay__raid-unrecorded-divider" />
      <p className="global-insights-overlay__raid-unrecorded-label">Why this matters</p>
      <p className="global-insights-overlay__raid-unrecorded-text">
        {item.why_it_matters ?? "Not available"}
      </p>
    </>
  );
}

function getNetworkObservationTitle(item: RaidDependenciesNetworkObservationDto) {
  const title = item.title?.trim();
  return title ?? item.observation_type ?? "Not available";
}

function ClashSeverityBadge({ value }: { value?: string | null }) {
  return <Badge value={value ?? "Not available"} type={getSeverityBadgeType(value)} size="sm" />;
}

function ConfidenceValueBadge({ value }: { value?: string | null }) {
  return (
    <Badge value={`Confidence: ${value ?? "Not available"}`} type="primary-outlined" size="sm" />
  );
}

function NetworkObservationTypeBadge({ value }: { value?: string | null }) {
  return <Badge value={value ?? "Not available"} type={getObservationBadgeType(value)} size="sm" />;
}

function getSeverityBadgeType(severity?: string | null) {
  const normalizedSeverity = severity?.toLowerCase() ?? "";
  if (normalizedSeverity === "critical") return "danger";
  if (normalizedSeverity === "high") return "warning";
  if (normalizedSeverity === "medium") return "info";
  return "primary-outlined";
}

function getObservationBadgeType(observationType?: string | null) {
  const normalizedType = observationType?.toLowerCase() ?? "";
  if (normalizedType.includes("circular")) {
    return "danger";
  }
  if (normalizedType.includes("bottleneck")) {
    return "warning";
  }
  return "info";
}

function TagCollection({ values, emptyText }: { values: string[]; emptyText: string }) {
  if (values.length === 0) {
    return <p className="global-insights-overlay__raid-unrecorded-text">{emptyText}</p>;
  }

  return (
    <div className="global-insights-overlay__raid-unrecorded-tags">
      {values.map((value) => (
        <Tag
          key={value}
          label={value}
          type="outlined"
          size="sm"
          closable={false}
          color={getTagColor(value)}
        />
      ))}
    </div>
  );
}

export default AiInsightsRaidDependenciesShelf;
